import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import type { MultiValue } from 'react-select'
import {
  faBuildingColumns,
  faBuildings,
  faLaptopMobile,
  faTrademark,
  faWarehouse,
} from '@fortawesome/pro-solid-svg-icons'
import type { CurrentRefinementsConnectorParamsRefinement } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements'
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import { DateTime } from 'luxon'

import { FilterColumn } from 'Components/FilterColumn/FilterColumn'

import { MultidatepickerFilter } from 'Algolia/filters/MultidatepickerFilter/MultidatepickerFilter'
import { MultiselectFilter } from 'Algolia/filters/MultiselectFilter/MultiselectFilter'
import { RadioFilter } from 'Algolia/filters/RadioFilter/RadioFilter'

import { getReturnCasePortalStatusTranslationId } from 'Portal/src/utilities/getReturnCasePortalStatus/getReturnCasePortalStatus.utils'

import type { ReturnsFilterColumnProps } from './ReturnsFilterColumn.types'

export const ReturnsFilterColumn = (props: ReturnsFilterColumnProps) => {
  const { onCloseFilterColumn, isVisible } = props
  const intl = useIntl()
  const today = DateTime.now()
    .startOf('day')
    .set({ hour: 0, minute: 0, second: 0 })

  const transformReturnCasePortalStatusItems = useCallback(
    (items: RefinementListItem[]) => {
      const transformedItems = items.reduce<RefinementListItem[]>(
        (result, item) => {
          const labelTranslationId = getReturnCasePortalStatusTranslationId(
            item.value,
          )

          item.label = intl.formatMessage({
            id: labelTranslationId,
          })

          return [...result, item]
        },
        [],
      )

      return transformedItems
    },
    [intl],
  )

  const returnCasePortalStatusValueMapper = useCallback(
    (
      result: MultiValue<RefinementListItem>,
      item: CurrentRefinementsConnectorParamsRefinement,
    ) => {
      const labelTranslationId = getReturnCasePortalStatusTranslationId(
        item.value.toString(),
      )

      const transformedItem: RefinementListItem = {
        count: item.count ?? 0,
        isRefined: true,
        label: intl.formatMessage({
          id: labelTranslationId,
        }),
        value: item.value.toString(),
      }

      return [...result, transformedItem]
    },
    [intl],
  )

  return (
    <FilterColumn isVisible={isVisible} onClose={onCloseFilterColumn}>
      <MultiselectFilter
        filterProps={{
          attribute: 'return_case.portal_status',
          transformItems: transformReturnCasePortalStatusItems,
        }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_return_case_portal_status',
        })}
        selectProps={{
          iconProps: {
            icon: faTrademark,
          },
        }}
        valueMapper={returnCasePortalStatusValueMapper}
      />

      <MultidatepickerFilter
        autoComplete="off"
        filterProps={{ attribute: 'return_case.created' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_return_case_created',
        })}
        multidatePickerProps={{
          /* eslint-disable sort-keys-fix/sort-keys-fix */
          customToolbarConfig: {
            today: {
              scope: 'today',
            },
            last_7days: {
              length: 7,
              period: 'days',
              scope: 'past',
            },
            last_30days: {
              length: 30,
              period: 'days',
              scope: 'past',
            },
            last_6months: {
              length: 6,
              period: 'months',
              scope: 'past',
            },
          },
          /* eslint-enable sort-keys-fix/sort-keys-fix */
          maxDate: new Date().setHours(23, 59, 59),
        }}
      />

      <MultidatepickerFilter
        autoComplete="off"
        filterProps={{ attribute: 'return_case.employee.exit_date' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_return_case_employee_exit_date',
        })}
      />

      <RadioFilter
        checkboxButtonGroupProps={{
          checkboxButtons: [
            {
              label: '2-4 weeks',
              value: encodeURI(
                JSON.stringify({
                  end: today
                    .minus({ weeks: 2 })
                    .set({ hour: 23, minute: 59, second: 59 })
                    .toSeconds(),
                  start: today
                    .minus({ weeks: 4 })
                    .plus({ days: 1 })
                    .toSeconds(),
                }),
              ),
            },
            {
              label: '4-6 weeks',
              value: encodeURI(
                JSON.stringify({
                  end: today
                    .minus({ weeks: 4 })
                    .set({ hour: 23, minute: 59, second: 59 })
                    .toSeconds(),
                  start: today
                    .minus({ weeks: 6 })
                    .plus({ days: 1 })
                    .toSeconds(),
                }),
              ),
            },
            {
              label: '6+ weeks',
              value: encodeURI(
                JSON.stringify({
                  end: today
                    .minus({ weeks: 6 })
                    .set({ hour: 23, minute: 59, second: 59 })
                    .toSeconds(),
                  start: -1,
                }),
              ),
            },
          ],
          name: 'openSince',
        }}
        filterProps={{ attribute: 'return_case.open_since' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_return_case_open_since',
        })}
      />

      <MultiselectFilter
        filterProps={{ attribute: 'return_case.stocklist_item.manufacturer' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_return_case_stocklist_item_manufacturer',
        })}
        selectProps={{
          iconProps: {
            icon: faTrademark,
          },
        }}
      />

      <MultiselectFilter
        filterProps={{ attribute: 'return_case.stocklist_item.codename' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_return_case_stocklist_item_codename',
        })}
        selectProps={{
          iconProps: {
            icon: faLaptopMobile,
          },
        }}
      />

      <MultiselectFilter
        filterProps={{ attribute: 'return_case.return_depot_name' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_return_case_return_depot_name',
        })}
        selectProps={{
          iconProps: {
            icon: faWarehouse,
          },
        }}
      />

      <MultiselectFilter
        filterProps={{ attribute: 'return_case.employee.office' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_return_case_employee_office',
        })}
        selectProps={{
          iconProps: {
            icon: faBuildings,
          },
        }}
      />

      <MultiselectFilter
        filterProps={{ attribute: 'return_case.rental.cost_center' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_return_case_rental_cost_center',
        })}
        selectProps={{
          iconProps: {
            icon: faBuildingColumns,
          },
        }}
      />
    </FilterColumn>
  )
}
